import { memo, useEffect, useRef } from "react";
import { dayjs, getAssetsUrl } from "utils";
import { useSnapshot } from "valtio/react";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/DetailsCommon/Forecast/PricePrediction/PricePredictionChart";
import {
  createForecastLineChart,
  ICreateForecastLineChartParams,
} from "@components/createChart";
import { useD3Ready } from "@hooks/useD3Ready";
import { rootStore } from "@store/rootStore";
import { ospThemes } from "@ospweb/sdk-ui";
import { redirectToLoginPage } from "@features/login/redirectToLoginPage";

const I18n = I18nUtil.createScoped(Translations);

export interface IPricePredictionChartProps extends Pick<
  ICreateForecastLineChartParams,
  "XAxis" | "ticks" | "forecast"
> {
  YAxis: number[];
  last_updated: number;
  name: string;
  logo?: string;
  type?: "stock" | "token";
}

const PricePredictionChart = ({
  XAxis,
  YAxis,
  ticks,
  last_updated,
  forecast,
  name,
  logo,
  type = "token",
}: IPricePredictionChartProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const d3Ready = useD3Ready();
  useEffect(() => {
    if (!d3Ready) {
      return;
    }
    if (ref.current && XAxis.length && YAxis.length && ticks.length) {
      const text1 = I18n.t("Your trading deserves better.");
      const text2 = I18n.t("Login to access this.");
      const buttonText = I18n.t("Start Trial");
      createForecastLineChart({
        config: {
          el: ref.current,
        },
        XAxis,
        YAxis: {
          name,
          color: ospThemes.colors.content_brand_primary,
          shadow: true,
          data: YAxis,
          logo: logo || getAssetsUrl("/assets/images/image_place_holder.png"),
        },
        ticks,
        // forecast: isLoggedIn ? forecast : undefined,
        forecast,
        type,
        forecastTimeLabel: dayjs(last_updated)
          .add(12, "month")
          .tz("America/New_York")
          .format("MMM DD YY"),
        showMemberMask: false,
        // showMemberMask: false,
        memberMask: `
        <div class="h-full w-full flex justify-center items-center px-4 [background-size:100%_100%]" style="background-image: url(${getAssetsUrl("/assets/images/forecastmask.webp")})">
          <div class="flex flex-col items-center gap-2">
            <div class="body_s text-content_primary flex flex-col items-center">
              <span>${text1}</span>
              <span>${text2}</span>
            </div>
            <button data-openreplay-label="misc_pricepredictionchart_click" class="button emphasis_button xs_button button_s">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M8.10774 2L7.32396 5.14999H9.57396L4.06835 11L5.29999 6.05H3.5L4.50771 2H8.10774Z" fill="#1ED1E9"/>
              </svg>
              ${buttonText}
            </button>
          </div>
        </div>`,
        onLogin: () => {
          redirectToLoginPage();
        },
      });
    }
  }, [d3Ready, XAxis, YAxis, ticks, isLoggedIn]);
  if (!XAxis.length || !YAxis.length || !ticks.length) {
    return null;
  }
  return (
    <div
      data-horizontal-scroll="true"
      className="md:p-6 p-4 border border-border_white_transparent10  w-full rounded-2"
    >
      <div
        ref={ref}
        className="h-39.5 w-full rounded-xl bg_background_white_overlay5 flex items-center justify-center text-content_tertiary"
      />
    </div>
  );
};

export default memo(PricePredictionChart);
